// src/components/tabs/Overview.js
import React, { useMemo, useState } from "react";
import AbatementPathwayGraph from "../dashboard/AbatementPathwayGraph";
import EnergyDemandReductionCard from "../dashboard/EnergyDemandReductionCard";
import NetZeroBalance2050Card from "../dashboard/NetZeroBalance2050Card";
import { getCurrentScenarioInfo, CITIZENS_PANEL_KEY, STANDARD_SCENARIO_SUFFIX } from "../../utils/scenarios";
import { useOverallReduction } from "../../hooks/useOverallReduction";
import EmissionsReductionSimple from "../dashboard/EmissionsReductionSimple";

// helpers to mirror family/suffix of parent scenarioKey
const parseFamily = (key) => {
  const hasDVL = key?.includes("_dvl_");
  const base = hasDVL ? "net_zero_futures_dvl" : "net_zero_futures";
  const suffix = key?.match(/_(\d{4})$/)?.[0] ?? ""; // e.g. "_2208", "_0509"
  return { base, suffix };
};
const buildKey = (base, name, ambition, suffix) => `${base}_${name}_${ambition}${suffix}`;

export default function Overview({
  sectorImpact,
  calculateTotalImpact,
  scenarioKey,                         // primary scenario from parent
  energyDemandReductionPercentage,
  energyDemandData,
  energyDemandOptions,
  netZeroBalanceData,
  netZeroBalanceOptions,
  emissionsProjection,
}) {
  const totalPct = Number(calculateTotalImpact(sectorImpact) ?? 0).toFixed(1);

  const totalImpactPct = emissionsProjection?.reductionPercentage ?? null;

  const { totalPct: overallPct, targetYear, loading: overallLoading } = useOverallReduction({
    scenario: scenarioKey,
    compareYear: 2025,
  });

  // keep if used elsewhere
  const currentScenarioInfo = useMemo(
    () => getCurrentScenarioInfo(sectorImpact),
    [sectorImpact]
  );

  // family/suffix from the primary key
  const { base, suffix } = useMemo(() => parseFamily(scenarioKey), [scenarioKey]);

  // local comparison state
  const [selectedComparisonValue, setSelectedComparisonValue] = useState(""); // "shift-high", etc.

  const comparisonScenarioKey = useMemo(() => {
    if (!selectedComparisonValue) return null;
    // Special case for citizens-panel
    if (selectedComparisonValue === "citizens-panel") return CITIZENS_PANEL_KEY;
    const [name, ambition = "low"] = selectedComparisonValue.split("-");
    // Use standard suffix for comparison scenarios (not the primary scenario's suffix)
    return buildKey(base, name, ambition, STANDARD_SCENARIO_SUFFIX);
  }, [selectedComparisonValue, base]);

  const comparisonLabel = selectedComparisonValue
    ? selectedComparisonValue.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())
    : undefined;

  const handleChangeComparison = (value) => setSelectedComparisonValue(value);

  //  citizens-panel: net_zero_futures_dvl_shift_high_transstshiftec_2001

  return (
    <div>
      <div className="text-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-3">Path to Net Zero</h2>
        <p className="text-sm text-gray-600 mb-4 max-w-2xl mx-auto">
          Explore the impact of your choices on reducing energy demand on how we can get to net zero.
        </p>
        <div className="bg-yellow-50 border-yellow-100 p-2 sm:p-3 rounded-lg border inline-block max-w-sm sm:max-w-md mx-auto">
          <EmissionsReductionSimple
           scenario={scenarioKey}  // e.g. "net_zero_futures_dvl_shift_low_2208"
            from={2025}
            to={2050}
            compareYear={2025}
          />
        </div>
      </div>

      {/* Energy Demand Reduction — scenario-driven via GraphQL */}
      <div className="flex flex-col gap-6">
        <EnergyDemandReductionCard
          scenario={scenarioKey}
          from={2025}
          to={2050}
          compareYear={2025}
          // comparison overlay props (pass both common names + label)
          comparisonScenarioKey={comparisonScenarioKey || undefined}
          comparisonScenario={comparisonScenarioKey || undefined}
          comparisonLabel={comparisonLabel}
          selectedComparisonValue={selectedComparisonValue}
          onChangeComparison={handleChangeComparison}
        />

        <div className="mb-4 flex flex-col items-center gap-3">
          <label className="text-xs text-gray-600">Add additional scenario:</label>
          <select
            value={selectedComparisonValue}
            onChange={(e) => handleChangeComparison(e.target.value)}
            className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">None</option>
            {/* <option value="current-low">Current</option> */}
                <option value="steer-low">Less change + Steer</option>
                <option value="shift-low">Less change + Shift</option>
                <option value="transform-low">Less change + Transform</option>
                <option value="steer-high">More change + Steer</option>
                <option value="shift-high">More change + Shift</option>
                <option value="transform-high">More change + Transform</option>
                <option value="citizens-panel">Citizens Panel</option>
          </select>
        </div>

        <NetZeroBalance2050Card
          year={2050}
          scenario={scenarioKey}
          yAxisTitle="Contribution to Net Zero (%)"
          unitSuffix=""
          unitScale={1}
        />
      </div>
     <div className="mt-12 w-full">
      <AbatementPathwayGraph
        scenario={scenarioKey}
        // If AbatementPathwayGraph supports overlays, you can pass:
        // comparisonScenarioKey={comparisonScenarioKey || undefined}
        // comparisonScenario={comparisonScenarioKey || undefined}
        // comparisonLabel={comparisonLabel}
        from={2025}
        to={2050}
      />
      </div>
    </div>
  );
}
